import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Save, X } from "lucide-react";

const empty = { title: "", duration: "", price: 0, deposit: 0, description: "", order: 0, highlight: false };

export default function AdminRates() {
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => http.get("/rates").then((r) => setRates(r.data));

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") {
        await http.post("/rates", form);
        toast.success("Created");
      } else {
        await http.put(`/rates/${editing}`, form);
        toast.success("Updated");
      }
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this rate?")) return;
    try { await http.delete(`/rates/${id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(formatError(e)); }
  };

  const startEdit = (r) => { setEditing(r.id); setForm({ ...r }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const cls = "w-full bg-noir-base border border-rosegold/20 px-3 py-2 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne";

  return (
    <div data-testid="admin-rates-page">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Rates</div>
          <h1 className="font-display text-4xl text-champagne">Services & <span className="italic text-pink-hot">pricing</span></h1>
        </div>
        <button
          onClick={() => { setEditing("new"); setForm({ ...empty, order: rates.length }); }}
          className="px-5 py-3 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow flex items-center gap-2"
          data-testid="add-rate-btn"
        >
          <Plus className="w-4 h-4" /> New Rate
        </button>
      </div>

      {editing && (
        <div className="glass-panel rounded-md p-6 mb-6 space-y-3" data-testid="rate-editor">
          <div className="grid md:grid-cols-2 gap-3">
            <input className={cls} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rate-title" />
            <input className={cls} placeholder="Duration (e.g. 1 Hour Incall)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="rate-duration" />
            <input className={cls} type="number" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} data-testid="rate-price" />
            <input className={cls} type="number" placeholder="Deposit ($)" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: parseFloat(e.target.value || 0) })} data-testid="rate-deposit" />
          </div>
          <textarea className={cls} rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-champagne/80">
            <input type="checkbox" checked={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} />
            Mark as "Most Requested"
          </label>
          <div className="flex gap-2">
            <button onClick={save} className="px-5 py-2 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow flex items-center gap-2" data-testid="rate-save">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={cancel} className="px-5 py-2 border border-rosegold/30 text-champagne text-xs tracking-[0.28em] uppercase rounded-sm flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {rates.map((r) => (
          <div key={r.id} className="glass-panel rounded-md p-5 flex justify-between items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.25em] uppercase text-rosegold">{r.duration}</div>
              <div className="font-display italic text-xl text-champagne">{r.title}</div>
              <div className="text-sm text-champagne/70 mt-1 line-clamp-2">{r.description}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl text-pink-hot">${r.price}</div>
              <div className="text-xs text-champagne/60">Dep ${r.deposit}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(r)} className="p-2 text-champagne/70 hover:text-pink-hot" data-testid={`edit-rate-${r.id}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(r.id)} className="p-2 text-champagne/70 hover:text-red-400" data-testid={`delete-rate-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
